/**
 * Session-35 — Bulletproof mobile detection.
 *
 * The initial fix used `ontouchstart` + `maxTouchPoints`, but some mobile
 * browsers under-report these (in-app browsers, older Chrome, WebView).
 * This helper layers 4 signals:
 *   1. UA sniff (mobile / phone / tablet keywords) — most reliable
 *   2. `pointer:coarse` matchMedia — designed for touch-primary devices
 *   3. `hover:none` matchMedia — desktop-only signal
 *   4. Legacy touch check as last resort
 *
 * Once decided, the answer is LOCKED into sessionStorage so back-navigation
 * (or any mid-session re-mount) cannot flip the layout. Only a fresh tab
 * or clearing sessionStorage re-evaluates.
 */
const SESSION_KEY = 'dws_is_mobile_locked';

export function isMobileDevice() {
  try {
    const cached = sessionStorage.getItem(SESSION_KEY);
    if (cached === '1') return true;
    if (cached === '0') return false;
  } catch {}

  let isMobile = false;
  try {
    const ua = (navigator.userAgent || '').toLowerCase();
    const uaHit = /iphone|ipod|ipad|android|blackberry|iemobile|opera mini|mobile|tablet|silk|kindle/i.test(ua);
    const coarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const hoverNone = window.matchMedia && window.matchMedia('(hover: none)').matches;
    const hasTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    isMobile = uaHit || coarsePointer || hoverNone || hasTouch;
  } catch { isMobile = false; }

  try { sessionStorage.setItem(SESSION_KEY, isMobile ? '1' : '0'); } catch {}
  return isMobile;
}

export function isPWAInstalled() {
  try {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  } catch { return false; }
}

/**
 * Returns true only when the current device should render the DESKTOP layout.
 * Any mobile / touch / PWA device is force-locked to the mobile layout.
 */
export function shouldRenderDesktop() {
  if (isMobileDevice()) return false;
  if (isPWAInstalled()) return false;
  return window.innerWidth >= 1024;
}
